import type { CSSProperties, JSX, ReactNode } from 'react';
import type { ModeId } from '@evi/skins';

// Shared primitives for the theme-picker popover — consumed by both
// `ThemePicker` (admin) and `DriverThemePicker` (PWA). Extracted so a
// tweak to row chrome or the mode segment propagates to both pickers.
// The two pickers MUST render identical row visuals; divergence goes
// in the top-level component, not here.

export function SectionLabel({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}): JSX.Element {
  return (
    <div
      style={{
        fontSize: 10,
        letterSpacing: 1.5,
        color: 'var(--evi-text-mute)',
        textTransform: 'uppercase',
        fontWeight: 500,
        padding: '6px 8px 8px',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function ModeSegment({
  value,
  onChange,
  allowed,
}: {
  value: ModeId;
  onChange: (m: ModeId) => void;
  /** Optional allow-list of modes to surface. Absent = both buttons
   *  (the default, 1fr 1fr). When it filters to a single mode, the
   *  segment renders one full-width button. Backward-compatible: the
   *  driver picker passes nothing today and is unaffected. */
  allowed?: ModeId[];
}): JSX.Element {
  const allItems: Array<{ id: ModeId; label: string; icon: JSX.Element }> = [
    { id: 'light', label: 'Light', icon: <SunIcon /> },
    { id: 'dark', label: 'Dark', icon: <MoonIcon /> },
  ];
  // Filter to the allowed modes; absent allow-list = both. A degenerate
  // empty/over-filtered result falls back to both so we never render an
  // empty segment.
  const filtered = allowed ? allItems.filter((it) => allowed.includes(it.id)) : allItems;
  const items = filtered.length > 0 ? filtered : allItems;
  return (
    <div
      role="group"
      aria-label="Mode"
      style={{
        display: 'grid',
        gridTemplateColumns: items.length === 1 ? '1fr' : '1fr 1fr',
        gap: 4,
        padding: 4,
        background: 'var(--evi-surface-2)',
        borderRadius: 8,
      }}
    >
      {items.map((it) => {
        const active = it.id === value;
        return (
          <button
            key={it.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(it.id)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '8px 12px',
              border: 'none',
              borderRadius: 6,
              background: active ? 'var(--evi-accent)' : 'transparent',
              // Active button paints over the theme's accent — read the
              // per-variant `--evi-on-accent` token (added in C13's
              // contract extension) so the foreground follows the
              // accent's luminance. Fallback #FFFFFF preserves the
              // long-standing white-text behaviour for any pre-paint
              // / SSR case and for themes that resolve onAccent to
              // white (Default, Cartographic, Luce, Neon, Obsidian).
              // Pre-fix this was hardcoded #FFFFFF and produced an
              // unreadable white-on-near-white button on Electric
              // Light Light (accent #FAFAFA), the same drift class as
              // the StickyCTA bug C13 fixed. Same fix shape; same
              // primitive shared between admin ThemePicker + driver
              // DriverThemePicker, so both pickers self-resolve.
              color: active ? 'var(--evi-on-accent, #FFFFFF)' : 'var(--evi-text)',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background 0.15s ease, color 0.15s ease',
            }}
          >
            {it.icon}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

export interface ThemeRowProps {
  name: string;
  description?: string;
  swatches: readonly [string, string, string];
  active: boolean;
  /** Lock icon + muted treatment when the driver doesn't own this theme. */
  locked?: boolean;
  onSelect: () => void;
}

/**
 * A single row in either picker's THEME section. Generic over what
 * "theme" means: the admin passes registry families; the driver passes
 * a mix of families and skin unlocks. The row itself doesn't know or
 * care — it just renders name / description / swatches / checkmark or
 * lock.
 */
export function ThemeRow({
  name,
  description,
  swatches,
  active,
  locked = false,
  onSelect,
}: ThemeRowProps): JSX.Element {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onSelect}
      onMouseEnter={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = 'var(--evi-surface-2)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          (e.currentTarget as HTMLElement).style.background = 'transparent';
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 10,
        alignItems: 'center',
        width: '100%',
        padding: 10,
        borderRadius: 8,
        border: 'none',
        cursor: 'pointer',
        background: active ? 'var(--evi-surface-2)' : 'transparent',
        color: 'var(--evi-text)',
        fontFamily: 'inherit',
        textAlign: 'left',
        transition: 'background 0.15s ease',
        // Locked rows read muted but stay tappable — tap routes to the
        // full themes page rather than applying.
        opacity: locked ? 0.65 : 1,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
        {description && (
          <div style={{ fontSize: 11, color: 'var(--evi-text-mute)' }}>{description}</div>
        )}
      </div>
      <div style={{ display: 'inline-flex', gap: 4 }}>
        {swatches.map((hex, i) => (
          <span
            key={i}
            aria-hidden
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: hex,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
        ))}
      </div>
      <span
        aria-hidden
        style={{
          width: 18,
          height: 18,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: locked ? 'var(--evi-text-mute)' : 'var(--evi-accent)',
          visibility: active || locked ? 'visible' : 'hidden',
        }}
      >
        {active ? <CheckIcon /> : locked ? <LockIcon /> : null}
      </span>
    </button>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────

export function PaletteIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
      <path
        d="M12 2C6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10 1 0 1.5-.5 1.5-1.5 0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.3 0-1 .83-1.8 1.83-1.8H16c3.3 0 6-2.7 6-6 0-4.6-4.5-8.2-10-8.2z"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <circle cx="6.5" cy="11.5" r="1.3" fill="var(--evi-good)" />
      <circle cx="9.5" cy="7" r="1.3" fill="var(--evi-info)" />
      <circle cx="14.5" cy="6.5" r="1.3" fill="var(--evi-power)" />
      <circle cx="17.5" cy="10.5" r="1.3" fill="var(--evi-warn)" />
    </svg>
  );
}

export function SunIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <line x1="4.9" y1="4.9" x2="6.8" y2="6.8" />
      <line x1="17.2" y1="17.2" x2="19.1" y2="19.1" />
      <line x1="4.9" y1="19.1" x2="6.8" y2="17.2" />
      <line x1="17.2" y1="6.8" x2="19.1" y2="4.9" />
    </svg>
  );
}

export function MoonIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}

export function CheckIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="5 12 10 17 19 7" />
    </svg>
  );
}

export function ClockIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8 L12 12 L15 14" />
    </svg>
  );
}

export function ChevronRightIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export function LockIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}
